<script setup>
import { i18n } from 'boot/i18n';
import { HrDate, HrIcon, HrIconActionBtn, HrPopupProxy, HrTextField } from 'src/design-system';
import { formatCalendarDate } from 'src/utils/DateUtils';
import { computed, ref } from 'vue';

/**
 * Text field with a calendar popup that reads and writes 'YYYY-MM-DD', shown in the app's locale.
 *
 * INTERIM — a design-system `HrDateField` is being built in parallel and should replace this.
 * The prop surface below is deliberately a subset of its sibling `HrTimeField`, using the same
 * names and defaults (`variant`, `modelValue`, `label`, `hint`, `disable`, `readonly`, `rules`,
 * `hideBottomSpace`, `dense`), so the swap is a tag rename at the two call sites in
 * EditCoverageDialog plus deleting this file.
 *
 * ONE THING TO CHECK BEFORE SWAPPING, and it does not fail loudly on its own: `HrTimeField` reads
 * and writes **Firestore Timestamps**. If `HrDateField` follows its sibling, it cannot be dropped
 * in as-is — coverage stores effective bounds as 'YYYY-MM-DD' calendar strings because the billing
 * projector compares them as calendar dates, and a Timestamp normalizes to null, silently turning
 * a coverage that ends into one that never does. Either `HrDateField` must offer a calendar-string
 * mode, or the call site must convert. `validateCoverage` raises UNREADABLE_EFFECTIVE_DATE if a
 * wrong type ever reaches the entity, so the mistake surfaces as a form error rather than a
 * mis-billed client — but it is far cheaper to catch it here.
 *
 * Why not the app's existing `DateInput`: it speaks Timestamps, for the same reason. `HrDate`'s
 * own contract is 'YYYY/MM/DD', so the slashes-vs-dashes conversion lives here rather than being
 * smuggled through an undeclared `mask` attribute.
 *
 * The field is read-only so a date can only be set from the calendar, never typed as free text.
 * That means Quasar's own `clearable` never renders — it requires an editable field — so clearing
 * is an explicit append action here. It stops propagation, or the field's click handler would
 * reopen the picker the moment the value was cleared.
 */
defineOptions({ name: 'CalendarDateField' });

const props = defineProps({
  /**
   * Visual container style derived from M3 text field variants.
   * @values filled, outlined
   */
  variant: {
    type: String,
    default: 'outlined',
    validator: (v) => ['filled', 'outlined'].includes(v),
  },
  /**
   * Selected date as a 'YYYY-MM-DD' calendar string, or null.
   *
   * NOT a Date and NOT a Firestore Timestamp — see the note above before swapping in HrDateField.
   */
  modelValue: {
    type: String,
    default: null,
  },
  /** Floating label text shown above the field. */
  label: {
    type: String,
    default: null,
  },
  /** Supporting text shown below the field. */
  hint: {
    type: String,
    default: null,
  },
  /** Shows a control that sets the value back to null. */
  /**
   * Earliest selectable date, as a 'YYYY-MM-DD' calendar string, or null for no lower bound.
   *
   * The calendar refuses an earlier date rather than a rule reporting one afterwards: a date that
   * cannot be picked needs no error message, and the field is read-only so there is no other way in.
   */
  min: {
    type: String,
    default: null,
  },
  clearable: {
    type: Boolean,
    default: false,
  },
  /** Prevents interaction and dims the field. */
  disable: {
    type: Boolean,
    default: false,
  },
  /**
   * Validation rules applied to the field. They receive the DISPLAYED date, not the
   * 'YYYY-MM-DD' model value — write a rule that needs the calendar string as a closure over
   * the bound value, as EditCoverageDialog does.
   */
  rules: {
    type: Array,
    default: () => [],
  },
  /** Suppresses the reserved space below the field for hint/error messages. */
  hideBottomSpace: {
    type: Boolean,
    default: true,
  },
  /** Reduces the field height to the M3 dense density level. */
  dense: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Emitted on change; payload is a 'YYYY-MM-DD' string, or null when the field is cleared. */
  'update:modelValue': null,
});

const isPickerOpen = ref(false);

const pickerValue = computed(() => {
  return props.modelValue === null ? null : props.modelValue.replaceAll('-', '/');
});

// The model stays a calendar string while the field shows the app's locale format — the same
// conversion CoverageList makes, so the editor and the list read alike. The fallback to the raw
// value is what keeps a date formatCalendarDate cannot read visible next to the rule that flags
// it; rendering an empty field would leave the error pointing at nothing.
/**
 * `hr-date` speaks 'YYYY/MM/DD', so the bound is compared in that form rather than the model's.
 * Zero-padded calendar strings sort lexicographically, which is what lets this be a string compare.
 */
const selectableDates = computed(() => {
  if (props.min === null) {
    return null;
  }
  const earliest = props.min.replaceAll('-', '/');

  return (date) => date >= earliest;
});

const displayValue = computed(() => {
  return formatCalendarDate(props.modelValue) ?? props.modelValue;
});

function onPick(value) {
  emit('update:modelValue', value === null || value === undefined ? null : value.replaceAll('/', '-'));
  isPickerOpen.value = false;
} // onPick()

function onFieldClick() {
  if (!props.disable) {
    isPickerOpen.value = true;
  }
} // onFieldClick()

function onClear() {
  emit('update:modelValue', null);
  isPickerOpen.value = false;
} // onClear()

const canClear = computed(() => {
  return props.clearable && !props.disable && props.modelValue !== null && props.modelValue !== '';
});
</script>

<template>
  <hr-text-field
    :model-value="displayValue"
    :variant="variant"
    :label="label"
    :hint="hint"
    :disable="disable"
    :rules="rules"
    :hide-bottom-space="hideBottomSpace"
    :dense="dense"
    readonly
    @click="onFieldClick"
  >
    <template #prepend>
      <hr-icon name="event" />
      <hr-popup-proxy
        v-model="isPickerOpen"
        cover
        transition-show="scale"
        transition-hide="scale"
      >
        <hr-date
          :model-value="pickerValue"
          :options="selectableDates"
          @update:model-value="onPick"
        />
      </hr-popup-proxy>
    </template>

    <template
      v-if="canClear"
      #append
    >
      <hr-icon-action-btn
        icon="close"
        size="sm"
        :aria-label="i18n.t('common.buttonClear')"
        data-e2e="btn-clear-date"
        @click.stop="onClear"
      />
    </template>
  </hr-text-field>
</template>
